import { useEffect } from "react";
import { parseClipboardElements } from "../../../features/canvas/engine/clipboardOperations";
import { parseExcalidrawClipboard } from "../../../features/canvas/engine/excalidrawClipboard";
import { getClipboardFallbackText } from "./canvasClipboard";
import { isEditableTarget } from "./useCanvasKeyboard";

const getImageFileFromClipboard = (clipboardData) =>
  Array.from(clipboardData?.files ?? []).find((file) =>
    file.type.startsWith("image/")
  );

const readPastedElements = (clipboardData) => {
  const clipboardText =
    clipboardData?.getData("text/plain") || getClipboardFallbackText();

  return (
    parseClipboardElements(clipboardText) ??
    parseExcalidrawClipboard(clipboardText)
  );
};

/**
 * Cmd/Ctrl+V handling for the canvas: pasted image files become image
 * elements, copied canvas elements are re-inserted, Excalidraw clipboard
 * payloads are converted into native elements, and pastes aimed at editable
 * targets (like the text editor) stay native.
 * @param {{ onElementsPaste: Function, onImagePaste: Function }} params
 * @returns {void}
 */
export const useCanvasPaste = ({ onElementsPaste, onImagePaste }) => {
  useEffect(() => {
    const handlePaste = (event) => {
      if (isEditableTarget(event.target)) return;

      const imageFile = getImageFileFromClipboard(event.clipboardData);
      if (imageFile) {
        event.preventDefault();
        const reader = new FileReader();
        reader.onload = () => onImagePaste(String(reader.result));
        reader.readAsDataURL(imageFile);
        return;
      }
      const pastedElements = readPastedElements(event.clipboardData);
      if (!pastedElements) return;

      event.preventDefault();
      onElementsPaste(pastedElements);
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [onElementsPaste, onImagePaste]);
};
